import { useContext, useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import submitLoyaltyPointsBurnCalculationRequest from 'actions/loyalty/submitLoyaltyPointsBurnCalculationRequest'
import buildBurnKey from 'loyalty/lib/pointsCalculation/buildBurnKey'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import type { BurnBatchRequest } from 'api/loyalty/requestLoyaltyPointsBurnCalculationBatch'
import { SET_BUNDLED_BURN_BATCH_RESULT } from 'actions/actionConstants'

export type OfferPageBurnResult = {
  enabled: boolean
  pointsToBurn?: number
  priceInPoints?: number
  hideZeroCash?: boolean
}

// Allows callers to opt out of fetching/calculating burn points for a branch
// while still invoking the hook unconditionally to satisfy React hook rules.
interface UseOfferPageBurnPointsOptions {
  disabled?: boolean
  /**
   * Overrides the pointsToApply value read from OfferPageStateContext.
   * Lets surfaces with their own state (e.g. ExperienceOfferPageStateContext)
   * drive this hook without needing to live inside OfferPageStateContext.
   */
  pointsToApplyOverride?: number
}

interface ResolveOfferPageBurnResultOptions {
  disabled?: boolean
  searchBurnEnabled: boolean
  priceInPoints?: number
  pointsToApply?: number
}

interface UseOfferPageBundledBurnPointsOptions {
  hotelPrice: number
  hotelLoyaltyProductType: App.LoyaltyProductType
  hotelBurnMargin?: string
  flightPrice: number
  shouldUseSplitBurn: boolean
  searchBurnEnabled?: boolean
}

export function resolveOfferPageBurnResult({
  disabled = false,
  searchBurnEnabled,
  priceInPoints,
  pointsToApply,
}: ResolveOfferPageBurnResultOptions): OfferPageBurnResult {
  if (disabled || !searchBurnEnabled || priceInPoints === undefined) {
    return { enabled: false }
  }

  const pointsToBurn = pointsToApply !== undefined
    ? Math.min(pointsToApply, priceInPoints)
    : priceInPoints

  return {
    enabled: true,
    pointsToBurn,
    priceInPoints,
    hideZeroCash: pointsToBurn >= priceInPoints,
  }
}

export function useOfferPageBundledBurnPoints({
  hotelPrice,
  hotelLoyaltyProductType,
  hotelBurnMargin,
  flightPrice,
  shouldUseSplitBurn,
  searchBurnEnabled,
}: UseOfferPageBundledBurnPointsOptions): OfferPageBurnResult {
  const dispatch = useAppDispatch()
  const { pointsToApply, charterBundleEnabled } = useContext(OfferPageStateContext)
  const globalSearchBurnEnabled = useAppSelector(state => state.loyalty.hasSelectedBurnToggle)
  const effectiveSearchBurnEnabled = searchBurnEnabled ?? globalSearchBurnEnabled
  const customerId = useAppSelector(state => state.auth.account.memberId ?? '')

  const hotelBurnResult = useOfferPageBurnPoints(
    hotelPrice,
    hotelLoyaltyProductType,
    hotelBurnMargin,
  )
  const flightBurnResult = useOfferPageBurnPoints(
    flightPrice,
    'int_economy',
    undefined,
    { disabled: !shouldUseSplitBurn },
  )

  const bundleKey = useMemo(() => {
    if (!shouldUseSplitBurn || !customerId) return undefined
    const hotelKey = buildBurnKey({ price: hotelPrice, loyaltyProductType: hotelLoyaltyProductType, mx: hotelBurnMargin }, customerId)
    const flightKey = buildBurnKey({ price: flightPrice, loyaltyProductType: 'int_economy' }, customerId)
    return `bundle:${hotelKey}:${flightKey}`
  }, [shouldUseSplitBurn, hotelPrice, hotelLoyaltyProductType, hotelBurnMargin, flightPrice, customerId])

  const hotelInPoints = hotelBurnResult.priceInPoints
  const flightInPoints = flightBurnResult.priceInPoints
  const combinedPriceInPoints = shouldUseSplitBurn && hotelInPoints !== undefined && flightInPoints !== undefined
    ? hotelInPoints + flightInPoints
    : undefined

  useEffect(() => {
    if (!bundleKey || combinedPriceInPoints === undefined) return
    dispatch({ type: SET_BUNDLED_BURN_BATCH_RESULT, burnBatchKey: bundleKey, priceInPoints: combinedPriceInPoints })
  }, [dispatch, bundleKey, combinedPriceInPoints])

  return useMemo(() => {
    if (charterBundleEnabled) return { enabled: false }
    if (!shouldUseSplitBurn) {
      return hotelBurnResult
    }

    return resolveOfferPageBurnResult({
      searchBurnEnabled: effectiveSearchBurnEnabled,
      pointsToApply,
      priceInPoints: combinedPriceInPoints,
    })
  }, [
    charterBundleEnabled,
    shouldUseSplitBurn,
    hotelBurnResult,
    combinedPriceInPoints,
    effectiveSearchBurnEnabled,
    pointsToApply,
  ])
}

/**
 * Calculates how many points to burn for a given price on the offer page,
 * based on the current burn toggle/slider state.
 *
 * Mirrors useSearchBurnPoints but reads pointsToApply from OfferPageState
 * instead of SearchBurnContext, unless `pointsToApplyOverride` is passed —
 * see that option for surfaces (e.g. Experience) with their own state.
 */
export default function useOfferPageBurnPoints(
  price: number,
  loyaltyProductType: App.LoyaltyProductType,
  mx?: string,
  options: UseOfferPageBurnPointsOptions = {},
): OfferPageBurnResult {
  const { disabled: disabledOption = false, pointsToApplyOverride } = options
  const dispatch = useAppDispatch()
  const { pointsToApply: contextPointsToApply, charterBundleEnabled } = useContext(OfferPageStateContext)
  const pointsToApply = pointsToApplyOverride !== undefined ? pointsToApplyOverride : contextPointsToApply
  const disabled = disabledOption || charterBundleEnabled
  const searchBurnEnabled = useAppSelector(state => state.loyalty.hasSelectedBurnToggle)
  const customerId = useAppSelector(state => state.auth.account.memberId ?? '')

  const calculationRequest: BurnBatchRequest = useMemo(() => ({
    price,
    loyaltyProductType,
    mx,
  }), [price, loyaltyProductType, mx])

  const batchKey = useMemo(() => {
    if (disabled || !searchBurnEnabled) return undefined
    return buildBurnKey(calculationRequest, customerId)
  }, [disabled, searchBurnEnabled, calculationRequest, customerId])

  const batchResult = useAppSelector(state => {
    if (!batchKey) return undefined
    return state.loyalty.pointsBurnBatchCalculations?.[batchKey]?.data
  })

  useEffect(() => {
    if (disabled || !searchBurnEnabled) return
    dispatch(submitLoyaltyPointsBurnCalculationRequest(calculationRequest))
  }, [disabled, dispatch, searchBurnEnabled, calculationRequest, customerId])

  return useMemo(() => {
    return resolveOfferPageBurnResult({
      disabled,
      searchBurnEnabled,
      priceInPoints: batchResult?.priceInPoints,
      pointsToApply,
    })
  }, [disabled, searchBurnEnabled, pointsToApply, batchResult])
}
